// =============================================================================
// app/src/lib/pagamentos.ts — o pagamento DIVIDIDO (migração 075) visto da tela.
//
// "Cem em dinheiro e o resto no cartão" (pedido do dono, 14/09/2026). No banco
// a venda fica com forma 'misto' e as partes em sale_payments; o caixa recebe
// uma entrada por parte, com a forma real. Aqui mora o que as TELAS precisam
// em comum e que não deve ser digitado três vezes com três arredondamentos:
//
//   * descreverPagamento — o texto da lista, do CSV e do caixa;
//   * casaFormas         — o filtro de VÁRIAS formas da tela de Vendas
//                          (o mesmo pedido de 14/09), que enxerga as partes;
//   * conferirPartes     — a conta da tela de venda, em CENTAVOS, para a soma
//                          fechar do mesmo jeito que a RPC vai conferir;
//   * parteEmDinheiro    — o troco no caixa é sobre a parte em dinheiro.
//
// Tudo função pura, com teste. A régua do dinheiro em pt-BR é a de
// lib/formato (paraNumero): "1.500" é mil e quinhentos, não um e meio.
// =============================================================================
import type { PaymentMethod } from '@core/types';
import { brl, paraNumero } from './formato';
import { rotuloDaForma } from './rotulos';

/** Uma parte como a lista recebe do banco (sale_payments embutido). */
export interface PartePaga {
  forma_pagamento: PaymentMethod;
  valor: number;
}

/**
 * As formas que DIVIDEM: dinheiro de verdade. Fiado é dívida (ledger próprio
 * em contas_receber), o vale abate antes da forma (store_credit_uses) e
 * 'misto' é o rótulo da divisão — o banco recusa os três numa parte.
 */
export const FORMAS_QUE_DIVIDEM: readonly PaymentMethod[] = [
  'dinheiro', 'pix', 'debito', 'credito', 'transferencia', 'boleto',
];

/**
 * "Misto (Dinheiro R$ 100,00 + Crédito R$ 50,00)" na venda dividida; o rótulo
 * da forma nas outras; traço quando não há forma (histórico antigo).
 */
export function descreverPagamento(
  forma: PaymentMethod | string | null | undefined,
  partes: ReadonlyArray<PartePaga> | null | undefined,
): string {
  const lista = partes ?? [];
  if (forma === 'misto' && lista.length > 0) {
    const texto = lista
      .map((p) => `${rotuloDaForma(p.forma_pagamento)} ${brl(Number(p.valor))}`)
      .join(' + ');
    return `${rotuloDaForma('misto')} (${texto})`;
  }
  return rotuloDaForma(forma);
}

/**
 * O filtro de várias formas. Nenhuma escolhida = todas. A venda casa se a
 * forma dela foi escolhida — e a DIVIDIDA casa também se qualquer PARTE foi:
 * quem filtra por "dinheiro" quer ver a venda em que entrou dinheiro, mesmo
 * que o resto tenha ido no cartão. Escolher "misto" acha as divididas.
 */
export function casaFormas(
  forma: PaymentMethod | string | null | undefined,
  partes: ReadonlyArray<PartePaga> | null | undefined,
  escolhidas: ReadonlyArray<PaymentMethod>,
): boolean {
  if (escolhidas.length === 0) return true;
  if (forma && escolhidas.includes(forma as PaymentMethod)) return true;
  if (forma === 'misto') {
    return (partes ?? []).some((p) => escolhidas.includes(p.forma_pagamento));
  }
  return false;
}

/** Uma parte como o vendedor a digita: a forma e o texto do valor. */
export interface ParteDigitada {
  forma: PaymentMethod;
  valorTexto: string;
}

export interface ConferenciaDasPartes {
  /** Soma das partes válidas, em reais. */
  soma: number;
  /** O que falta (positivo) ou sobra (negativo) para fechar, em reais. */
  diferenca: number;
  /** Alguma parte sem valor, ou com valor que não é número maior que zero. */
  incompleta: boolean;
  /** Duas partes ou mais, todas válidas, somando exatamente o alvo. */
  fecha: boolean;
}

const centavos = (n: number): number => Math.round(n * 100);

/**
 * A conta em CENTAVOS: somar reais em ponto flutuante dá 149,99999 numa venda
 * de 150, e a RPC compara com `<>` depois de arredondar. Aqui a soma é inteira
 * e a comparação é exata — o que a tela diz "fecha" a RPC aceita.
 */
export function conferirPartes(
  partes: ReadonlyArray<ParteDigitada>,
  aPagar: number,
): ConferenciaDasPartes {
  let soma = 0;
  let incompleta = false;
  for (const p of partes) {
    const v = paraNumero(p.valorTexto);
    if (v === null || v <= 0) { incompleta = true; continue; }
    soma += centavos(v);
  }
  const alvo = centavos(aPagar);
  return {
    soma: soma / 100,
    diferenca: (alvo - soma) / 100,
    incompleta,
    fecha: !incompleta && partes.length >= 2 && alvo > 0 && soma === alvo,
  };
}

/**
 * O que falta para a parte `indice` fechar a conta, dadas as OUTRAS partes —
 * é o botão "resto" da tela. Texto pronto para o campo, com vírgula. Zero (ou
 * negativo) vira "0,00": o vendedor vê que as outras já cobrem tudo.
 */
export function restanteDaParte(
  partes: ReadonlyArray<ParteDigitada>,
  indice: number,
  aPagar: number,
): string {
  let outras = 0;
  partes.forEach((p, i) => {
    if (i === indice) return;
    const v = paraNumero(p.valorTexto);
    if (v !== null && v > 0) outras += centavos(v);
  });
  const resto = Math.max(centavos(aPagar) - outras, 0);
  return (resto / 100).toFixed(2).replace('.', ',');
}

/** As partes no formato que a RPC recebe (p_pagamentos). Só as válidas —
 *  a tela não deixa confirmar enquanto `conferirPartes` não fechar. */
export function partesParaRpc(
  partes: ReadonlyArray<ParteDigitada>,
): Array<{ forma: PaymentMethod; valor: number }> {
  const saida: Array<{ forma: PaymentMethod; valor: number }> = [];
  for (const p of partes) {
    const v = paraNumero(p.valorTexto);
    if (v !== null && v > 0) saida.push({ forma: p.forma, valor: centavos(v) / 100 });
  }
  return saida;
}

/**
 * Quanto DESTA venda passou pela gaveta — a base do troco na conferência do
 * caixa (055). Venda em dinheiro: o total. Dividida: só a parte em dinheiro.
 * Pix e cartão: nada — não há o que contar.
 */
export function parteEmDinheiro(
  forma: PaymentMethod | string | null | undefined,
  partes: ReadonlyArray<PartePaga> | null | undefined,
  total: number,
): number {
  if (forma === 'dinheiro') return total;
  if (forma === 'misto') {
    return (partes ?? [])
      .filter((p) => p.forma_pagamento === 'dinheiro')
      .reduce((s, p) => s + centavos(Number(p.valor)), 0) / 100;
  }
  return 0;
}
